// /components/ui/ToastSystem.jsx
// Einziger Toast-Host der App. Position oben, Z-Index über Modals, A11y.
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Toaster } from "react-hot-toast";
import { dismissAllToasts } from "../../utils/toast.js";
import { Z_INDEX } from "./zIndex.js";

/**
 * Zentrales Toast-System (exakt 1 Instanz im Root).
 * Position: oben, konfiguriert hier. Z-Index über Modals/Drawer.
 * Dedupe/Rate-Limit: in client/utils/toast.js.
 */
export default function ToastSystem() {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") dismissAllToasts();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const mobileSafeStyle = {
    maxWidth: "min(100%, 360px)",
    overflowWrap: "break-word",
    wordBreak: "break-word",
  };

  const defaultStyle = {
    borderRadius: "12px",
    background: "linear-gradient(180deg, rgba(11,19,36,0.95), rgba(11,19,36,0.92))",
    color: "#dbeafe",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
    fontWeight: 600,
    pointerEvents: "auto",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    ...mobileSafeStyle,
  };

  const iconSecondary = "#0b1324";

  const toastOptions = {
    duration: 3500,
    style: defaultStyle,
    className:
      "transition-all duration-300 transform data-[visible=true]:translate-y-0 data-[visible=false]:-translate-y-2",
    success: {
      iconTheme: { primary: "#10b981", secondary: iconSecondary },
      style: { borderColor: "rgba(16,185,129,0.25)", color: "#a7f3d0" },
    },
    error: {
      iconTheme: { primary: "#ef4444", secondary: iconSecondary },
      style: { borderColor: "rgba(239,68,68,0.25)", color: "#fecaca" },
    },
    loading: {
      iconTheme: { primary: "#3b82f6", secondary: iconSecondary },
      style: { borderColor: "rgba(59,130,246,0.25)", color: "#bfdbfe" },
    },
    info: {
      iconTheme: { primary: "#3b82f6", secondary: iconSecondary },
      style: { borderColor: "rgba(59,130,246,0.25)", color: "#93c5fd" },
    },
    warning: {
      iconTheme: { primary: "#f59e0b", secondary: iconSecondary },
      style: { borderColor: "rgba(245,158,11,0.25)", color: "#fde68a" },
    },
  };

  const node = (
    <Toaster
      position="top-center"
      gutter={10}
      reverseOrder={false}
      containerClassName="toast-portal"
      ariaProps={{ role: "status", "aria-live": "polite" }}
      containerStyle={{
        position: "fixed",
        top: "calc(env(safe-area-inset-top, 0px) + 12px)",
        left: 0,
        right: 0,
        zIndex: Z_INDEX.toast,
        padding: "0 max(16px, env(safe-area-inset-left, 0px)) 0 max(16px, env(safe-area-inset-right, 0px))",
        pointerEvents: "none",
      }}
      toastOptions={toastOptions}
    />
  );
  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
